import CustomerService from 'services/CustomerService';

export const calculateItemAndBatchQuantities = (items = []) => {
  const itemQtys = {};
  const batchQtys = {};

  for (const saleItem of items || []) {
    if (!saleItem) continue;
    const itemId = saleItem.item_id || saleItem.item?.id;
    const itemType = saleItem.item?.type;

    if (itemType === 'COMBO') {
      for (const subItem of saleItem.sub_item || []) {
        const subId = String(subItem.item_id || subItem.id);
        itemQtys[subId] = (itemQtys[subId] || 0) + Number(subItem.quantity || 0);
        for (const sb of subItem.batch_data || []) {
          if (sb.batch_id) {
            const bId = String(sb.batch_id);
            batchQtys[bId] = (batchQtys[bId] || 0) + Number(sb.quantity || 0);
          }
        }
      }
    } else if (itemId) {
      const idStr = String(itemId);
      itemQtys[idStr] = (itemQtys[idStr] || 0) + Number(saleItem.quantity || 0);
      for (const sb of saleItem.batch_data || []) {
        if (sb.batch_id) {
          const bId = String(sb.batch_id);
          batchQtys[bId] = (batchQtys[bId] || 0) + Number(sb.quantity || 0);
        }
      }
    }
  }

  return { itemQtys, batchQtys };
};

export const calculateOriginalItemQuantities = (items = [], products = []) => {
  const originalQtys = {};
  for (const si of items || []) {
    const itemId = si?.item_id || si?.item?.id;
    if (itemId) {
      const resolvedItem = (products || []).find((p) => String(p.id) === String(itemId));
      if (resolvedItem?.type === 'COMBO') {
        for (const subItem of resolvedItem.details?.sub_items || []) {
          const subId = String(subItem.item_id);
          const subQty = Number(subItem.fraction || 0) * Number(si.quantity || 0);
          originalQtys[subId] = (originalQtys[subId] || 0) + subQty;
        }
      } else {
        const idStr = String(itemId);
        originalQtys[idStr] = (originalQtys[idStr] || 0) + Number(si.quantity || 0);
      }
    }
  }
  return originalQtys;
};

export const calculateOriginalBatchQuantities = (items = []) => {
  const origQtys = {};
  for (const item of items || []) {
    for (const sb of item.batch_data || []) {
      if (sb.batch_id) {
        const bId = String(sb.batch_id);
        origQtys[bId] = (origQtys[bId] || 0) + Number(sb.quantity || 0);
      }
    }
    for (const si of item.sub_item || []) {
      for (const sb of si.batch_data || []) {
        if (sb.batch_id) {
          const bId = String(sb.batch_id);
          origQtys[bId] = (origQtys[bId] || 0) + Number(sb.quantity || 0);
        }
      }
    }
  }
  return origQtys;
};

export const calculateStockDeltas = ({
  items = [],
  originalItemQtys = {},
  originalBatchQtys = {},
  isEdit = false,
} = {}) => {
  const { itemQtys: currentItemQtys, batchQtys: currentBatchQtys } =
    calculateItemAndBatchQuantities(items);

  if (!isEdit) {
    return { itemDeltas: currentItemQtys, batchDeltas: currentBatchQtys };
  }

  const itemDeltas = {};
  const allItemIds = new Set([
    ...Object.keys(currentItemQtys),
    ...Object.keys(originalItemQtys || {}),
  ]);
  allItemIds.forEach((id) => {
    const delta = (currentItemQtys[id] || 0) - ((originalItemQtys || {})[id] || 0);
    if (delta !== 0) itemDeltas[id] = delta;
  });

  const batchDeltas = {};
  const allBatchIds = new Set([
    ...Object.keys(currentBatchQtys),
    ...Object.keys(originalBatchQtys || {}),
  ]);
  allBatchIds.forEach((id) => {
    const delta = (currentBatchQtys[id] || 0) - ((originalBatchQtys || {})[id] || 0);
    if (delta !== 0) batchDeltas[id] = delta;
  });

  return { itemDeltas, batchDeltas };
};

export const applyLocalStockUpdates = ({
  items = [],
  originalItemQtys = {},
  originalBatchQtys = {},
  isEdit = false,
  inventoryLocalStockUpdate,
  warehouseId,
  warehouseLocalStockUpdate,
  localBatches,
  setLocalBatches,
} = {}) => {
  const { itemDeltas, batchDeltas } = calculateStockDeltas({
    items,
    originalItemQtys,
    originalBatchQtys,
    isEdit,
  });

  if (inventoryLocalStockUpdate) {
    inventoryLocalStockUpdate({ itemDeltas, batchDeltas });
  }

  const currentWhId = warehouseId === 0 ? null : warehouseId;
  if (currentWhId && warehouseLocalStockUpdate) {
    warehouseLocalStockUpdate({ warehouseId: currentWhId, itemDeltas });
  }

  if (setLocalBatches && localBatches?.length > 0 && Object.keys(batchDeltas).length > 0) {
    setLocalBatches((prev) =>
      (prev || []).map((b) => {
        const key = String(b.id);
        if (batchDeltas[key]) {
          return {
            ...b,
            stock: (Number(b.stock) || 0) - (Number(batchDeltas[key]) || 0),
          };
        }
        return b;
      })
    );
  }

  return { itemDeltas, batchDeltas };
};

export const applyLocalCustomerUpdates = async ({
  selectedCustomer,
  localCustomerUpdate,
} = {}) => {
  const customerId = selectedCustomer?.id || selectedCustomer;
  if (!customerId) return selectedCustomer;

  try {
    const freshCustomer = await CustomerService.getCustomer(customerId);
    if (freshCustomer) {
      if (localCustomerUpdate) {
        localCustomerUpdate(customerId, freshCustomer);
      }
      return freshCustomer;
    }
  } catch (err) {
    console.error('Failed to fetch customer:', err);
  }

  return selectedCustomer;
};

export const applyLocalUpdatesAfterSale = async ({
  items = [],
  editData,
  products = [],
  originalBatchQtys,
  shop,
  warehouseId,
  inventoryLocalStockUpdate,
  warehouseLocalStockUpdate,
  localBatches,
  setLocalBatches,
  selectedCustomer,
  localCustomerUpdate,
  isEdit = false,
} = {}) => {
  const originalItemQtys = isEdit
    ? calculateOriginalItemQuantities(editData?.items, products)
    : {};

  applyLocalStockUpdates({
    items,
    originalItemQtys,
    originalBatchQtys,
    isEdit,
    inventoryLocalStockUpdate,
    warehouseId,
    warehouseLocalStockUpdate,
    localBatches,
    setLocalBatches,
  });

  return await applyLocalCustomerUpdates({
    selectedCustomer,
    localCustomerUpdate,
  });
};

const StockHelper = {
  calculateItemAndBatchQuantities,
  calculateOriginalItemQuantities,
  calculateOriginalBatchQuantities,
  calculateStockDeltas,
  applyLocalStockUpdates,
  applyLocalCustomerUpdates,
  applyLocalUpdatesAfterSale,
};

export default StockHelper;
